import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Send,
  Bitcoin,
  User,
  Phone,
  MapPin,
  ArrowLeft,
  CheckCircle2,
  Sparkles,
  Shield,
  Loader2,
  Check,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useCart } from '@/contexts/CartContext'
import { CustomerInfo, getActivityEmoji } from '@/types/cart.types'
import {
  buildSingleBookingPayload,
  buildGroupBookingPayload,
} from './cartBookingPayload'
import { validatePhoneNumber } from '@/lib/phoneValidation'
import BitcoinPayment from '@/components/BitcoinPayment'

interface FormErrors {
  name?: string
  phone?: string
}

export default function CartCheckoutForm() {
  const { t } = useTranslation('cart')
  const {
    items,
    customerInfo,
    setCustomerInfo,
    setCheckoutStep,
    getTotal,
    clearCart,
  } = useCart()

  // Get phone, name, and accommodation from cart items if available (from booking form)
  const phoneFromCart =
    items.find((item) => item.customerPhone)?.customerPhone || ''
  const nameFromCart =
    items.find((item) => item.guestDetails?.[0]?.name)?.guestDetails[0].name ||
    ''
  const accommodationFromCart =
    items.find((item) => item.accommodation)?.accommodation || ''

  const [formData, setFormData] = useState<CustomerInfo>({
    name: customerInfo?.name || nameFromCart,
    email: '', // Email not collected anymore
    phone: customerInfo?.phone || phoneFromCart,
    accommodation: customerInfo?.accommodation || accommodationFromCart,
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [showBitcoin, setShowBitcoin] = useState(false)
  const [isBooking, setIsBooking] = useState(false)
  const [bookingResult, setBookingResult] = useState<{
    success: boolean
    bookingNumbers?: string[]
    error?: string
  } | null>(null)

  // Fetch Bitcoin payment settings
  const { data: bitcoinSettings } = useQuery({
    queryKey: ['bitcoin-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'bitcoin_payment')
        .single()

      if (error) {
        console.error('Error fetching bitcoin settings:', error)
        return { enabled: false, address: '', confirmations_required: 1 }
      }

      return data?.value as {
        enabled: boolean
        address: string
        confirmations_required: number
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  const total = getTotal()

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = t('ckNameRequired')
    }

    // WhatsApp number is required and must have country code
    if (!formData.phone?.trim()) {
      newErrors.phone = t('ckPhoneRequired')
    } else {
      const phoneValidation = validatePhoneNumber(formData.phone)
      if (!phoneValidation.isValid) {
        newErrors.phone = phoneValidation.message!
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field: keyof CustomerInfo, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error on change
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const handleDirectBooking = async () => {
    if (!validateForm()) return

    // Save customer info
    setCustomerInfo(formData)

    setIsBooking(true)
    setBookingResult(null)

    try {
      const bookingNumbers: string[] = []

      // Split customer name for first/last (used for both single and group bookings)
      const nameParts = formData.name.trim().split(' ')
      const firstName = nameParts[0] || ''
      const lastName = nameParts.slice(1).join(' ') || ''
      const phone = formData.phone || ''
      const customer = {
        firstName,
        lastName,
        phone,
        fullName: formData.name,
        accommodation: formData.accommodation || null,
      }

      // Use GROUP BOOKING for multiple items, single booking for one item
      if (items.length > 1) {
        // GROUP BOOKING: Create one parent booking with all activities as children
        const groupPayload = buildGroupBookingPayload(items, customer)

        const response = await fetch('/api/group-bookings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(groupPayload),
        })

        if (!response.ok) {
          throw new Error('Failed to create group booking')
        }

        const result = await response.json()
        // All activities share the same booking number
        bookingNumbers.push(result.bookingNumber || result.booking_number)
      } else {
        // SINGLE BOOKING: Use regular booking endpoint
        const item = items[0]
        const payload = buildSingleBookingPayload(item, customer)

        const response = await fetch('/api/bookings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })

        if (!response.ok) {
          throw new Error(`Failed to create booking for ${item.activityTitle}`)
        }

        const result = await response.json()
        bookingNumbers.push(result.bookingNumber || result.booking_number)
      }

      setBookingResult({ success: true, bookingNumbers })

      // Clear cart after successful booking
      setTimeout(() => {
        clearCart()
      }, 3000)
    } catch (error) {
      console.error('Booking failed:', error)
      setBookingResult({
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Booking failed. Please try again.',
      })
    } finally {
      setIsBooking(false)
    }
  }

  const handleBitcoinCheckout = () => {
    if (!validateForm()) return

    // Save customer info
    setCustomerInfo(formData)

    // Show Bitcoin payment modal
    setShowBitcoin(true)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-5"
    >
      {/* Back button */}
      <motion.button
        onClick={() => setCheckoutStep('cart')}
        whileHover={{ x: -4 }}
        className="flex items-center gap-2 text-white/50 hover:text-white
                   transition-colors text-sm group"
      >
        <ArrowLeft className="h-4 w-4 group-hover:text-garage-accent transition-colors" />
        {t('ckBackToCart')}
      </motion.button>

      {/* Contact Information Form */}
      <div className="relative overflow-hidden rounded-2xl bg-white/5 border border-white/10 p-5">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-garage-accent/5 to-transparent pointer-events-none" />

        <div className="relative">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-garage-accent/20 to-orange-500/20 flex items-center justify-center border border-white/10">
              <User className="h-5 w-5 text-garage-accent" />
            </div>
            <div>
              <h3 className="font-bold text-white">{t('ckContactInfo')}</h3>
              <p className="text-white/40 text-xs">{t('ckContactSub')}</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="flex items-center gap-2 text-sm text-white/60 mb-2">
                <User className="h-3.5 w-3.5 text-garage-accent" />
                {t('ckFullName')} <span className="text-garage-accent">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={t('ckNamePlaceholder')}
                className={`w-full px-4 py-3.5 bg-white/5 border rounded-xl
                           text-white placeholder:text-white/30
                           focus:outline-none focus:ring-2 focus:ring-garage-accent/50 focus:border-garage-accent/50
                           transition-all duration-200
                           ${
                             errors.name
                               ? 'border-red-500/50 bg-red-500/5'
                               : 'border-white/10 hover:border-white/20'
                           }`}
              />
              <AnimatePresence>
                {errors.name && (
                  <motion.p
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="text-red-400 text-xs mt-2 flex items-center gap-1"
                  >
                    <span className="w-1 h-1 rounded-full bg-red-400" />
                    {errors.name}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            {/* WhatsApp Number */}
            <div>
              <label className="flex items-center gap-2 text-sm text-white/60 mb-2">
                <Phone className="h-3.5 w-3.5 text-garage-accent" />
                {t('ckWhatsapp')} <span className="text-garage-accent">*</span>
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => {
                  const val = e.target.value
                  handleInputChange('phone', val)
                  if (val.trim()) {
                    const v = validatePhoneNumber(val)
                    if (!v.isValid) {
                      setErrors((prev) => ({ ...prev, phone: v.message }))
                    }
                  }
                }}
                placeholder={t('ckPhonePlaceholder')}
                className={`w-full px-4 py-3.5 bg-white/5 border rounded-xl
                           text-white placeholder:text-white/30
                           focus:outline-none focus:ring-2 focus:ring-garage-accent/50 focus:border-garage-accent/50
                           transition-all duration-200
                           ${
                             errors.phone
                               ? 'border-red-500/50 bg-red-500/5'
                               : 'border-white/10 hover:border-white/20'
                           }`}
              />
              <AnimatePresence>
                {errors.phone && (
                  <motion.p
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="text-red-400 text-xs mt-2 flex items-center gap-1"
                  >
                    <span className="w-1 h-1 rounded-full bg-red-400" />
                    {errors.phone}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            {/* Accommodation */}
            <div>
              <label className="flex items-center gap-2 text-sm text-white/60 mb-2">
                <MapPin className="h-3.5 w-3.5 text-garage-accent" />
                {t('ckStaying')}
                <span className="text-white/30 text-xs">{t('ckOptional')}</span>
              </label>
              <input
                type="text"
                value={formData.accommodation}
                onChange={(e) =>
                  handleInputChange('accommodation', e.target.value)
                }
                placeholder={t('ckHotelPlaceholder')}
                className="w-full px-4 py-3.5 bg-white/5 border border-white/10 hover:border-white/20 rounded-xl
                           text-white placeholder:text-white/30
                           focus:outline-none focus:ring-2 focus:ring-garage-accent/50 focus:border-garage-accent/50
                           transition-all duration-200"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Order Summary */}
      <div className="relative overflow-hidden rounded-2xl bg-white/5 border border-white/10 p-5">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent pointer-events-none" />

        <div className="relative">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500/20 to-garage-accent/20 flex items-center justify-center border border-white/10">
              <Sparkles className="h-5 w-5 text-orange-400" />
            </div>
            <h3 className="font-bold text-white">{t('ckOrderSummary')}</h3>
          </div>

          <div className="space-y-3 mb-4">
            {items.map((item, index) => {
              const emoji = getActivityEmoji(item.activityType)
              const formattedDate = format(new Date(item.selectedDate), 'MMM d')

              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center text-sm">
                      {emoji}
                    </div>
                    <div>
                      <span className="text-white/90 text-sm font-medium truncate block max-w-[140px]">
                        {item.activityTitle}
                      </span>
                      <span className="text-white/40 text-xs">
                        {formattedDate} •{' '}
                        {t('ckPerson', { count: item.partySize })}
                      </span>
                    </div>
                  </div>
                  <span className="text-white font-semibold">
                    ${item.subtotal}
                  </span>
                </motion.div>
              )
            })}
          </div>

          <div className="border-t border-white/10 pt-4 flex items-center justify-between">
            <span className="text-white/60">{t('totalAmount')}</span>
            <div className="text-right">
              <span className="text-2xl font-bold bg-gradient-to-r from-garage-accent to-orange-400 bg-clip-text text-transparent">
                ${total}
              </span>
              <span className="text-white/40 text-sm ml-1">USD</span>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Success State */}
      {bookingResult?.success && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative overflow-hidden rounded-2xl bg-green-500/10 border border-green-500/30 p-6 text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <Check className="h-8 w-8 text-green-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">
            {t('ckConfirmed')}
          </h3>
          <div className="space-y-1 mb-4">
            {bookingResult.bookingNumbers?.map((num, i) => (
              <p key={i} className="text-green-400 font-mono font-bold text-lg">
                {num}
              </p>
            ))}
          </div>
          <p className="text-white/60 text-sm">
            {t('ckVisitPre')}{' '}
            <span className="text-white font-semibold">Lime Lounge</span>{' '}
            {t('ckVisitPost')}
            <br />
            {t('ckWhatsappConfirm')}
          </p>
        </motion.div>
      )}

      {/* Booking Error State */}
      {bookingResult?.error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative overflow-hidden rounded-2xl bg-red-500/10 border border-red-500/30 p-4"
        >
          <p className="text-red-400 text-sm text-center">
            {bookingResult.error}
          </p>
        </motion.div>
      )}

      {/* Payment Options */}
      {!bookingResult?.success && (
        <div className="space-y-3">
          {/* Book Now Button */}
          <motion.button
            onClick={handleDirectBooking}
            disabled={isBooking}
            whileHover={{ scale: isBooking ? 1 : 1.02 }}
            whileTap={{ scale: isBooking ? 1 : 0.98 }}
            className="relative w-full py-4 bg-gradient-to-r from-garage-accent to-orange-500
                     text-white font-bold text-lg rounded-xl overflow-hidden
                     transition-all flex items-center justify-center gap-3 group
                     shadow-lg shadow-garage-accent/20 hover:shadow-xl hover:shadow-garage-accent/30
                     disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {/* Shine effect on hover */}
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent
                          -translate-x-full group-hover:translate-x-full transition-transform duration-700"
            />
            {isBooking ? (
              <>
                <Loader2 className="h-6 w-6 animate-spin" />
                {t('ckCreating')}
              </>
            ) : (
              <>
                <Send className="h-6 w-6" />
                {t('ckBookNow')}
              </>
            )}
          </motion.button>

          {/* Bitcoin Button */}
          {bitcoinSettings?.enabled && bitcoinSettings?.address && (
            <motion.button
              onClick={handleBitcoinCheckout}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="relative w-full py-4 bg-gradient-to-r from-[#f7931a] to-[#ff9d00]
                       text-white font-bold text-lg rounded-xl overflow-hidden
                       transition-all flex items-center justify-center gap-3 group
                       shadow-lg shadow-[#f7931a]/20 hover:shadow-xl hover:shadow-[#f7931a]/30"
            >
              {/* Shine effect on hover */}
              <div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent
                            -translate-x-full group-hover:translate-x-full transition-transform duration-700"
              />
              <Bitcoin className="h-6 w-6" />
              {t('ckPayBitcoin')}
            </motion.button>
          )}

          {/* Trust indicators */}
          <div className="flex items-center justify-center gap-4 pt-2">
            <div className="flex items-center gap-1.5 text-white/30 text-xs">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-500/70" />
              <span>{t('ckInstant')}</span>
            </div>
            <div className="flex items-center gap-1.5 text-white/30 text-xs">
              <Shield className="h-3.5 w-3.5 text-garage-accent/70" />
              <span>{t('ck48h')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Bitcoin Payment Modal */}
      {showBitcoin && bitcoinSettings?.address && (
        <BitcoinPayment
          isOpen={showBitcoin}
          onClose={() => setShowBitcoin(false)}
          bookingData={{
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            totalPrice: total,
            activityTitle:
              items.length === 1
                ? items[0].activityTitle
                : `${items.length} Activities`,
          }}
          btcAddress={bitcoinSettings.address}
        />
      )}
    </motion.div>
  )
}
